import { Component } from "@theme/component";
import { prefersReducedMotion, reducedMotion } from "@theme/utilities";

/**
 * WEB-453: publish the combined height of the SD announcement bars in the header
 * group as `--sd-announcement-bar-offset`, so a transparent header leaves room for
 * them (see sd-announcement-bar.liquid). Measures the section wrappers, the same
 * boxes Horizon sums into `--header-group-height`, and re-reads the DOM each time
 * so a second bar or a theme-editor reload can't leave a stale value.
 */
function publishBarOffset() {
  let offset = 0;
  for (const bar of document.querySelectorAll("#header-group .sd-announcement-bar")) {
    const section = bar.closest(".shopify-section");
    if (section instanceof HTMLElement) offset += section.offsetHeight;
  }
  document.body.style.setProperty("--sd-announcement-bar-offset", `${offset}px`);
}

/**
 * SD Announcement Bar Web Component
 *
 * Handles auto-rotation and navigation for announcement slides.
 * Supports both carousel mode and ticker (continuous scroll) mode.
 *
 * Accessibility (WEB-337):
 * - Both motion modes (carousel auto-rotation and the ticker marquee) expose the
 *   same persistent, keyboard/touch-operable pause/play control (WCAG 2.2.2).
 * - Under prefers-reduced-motion both start paused; the control is the opt-in
 *   and can re-pause afterward (WCAG 2.3.3). The `playing` flag is the single
 *   source of truth; `#applyMotion()` maps it onto whichever mechanism is live.
 *   The OS preference is observed live via the shared `reducedMotion` query from
 *   @theme/utilities, so a mid-session change is honored (unless the visitor has
 *   already used the control).
 * - Inactive slides use `inert`, keeping them out of the tab order.
 *
 * @extends {Component}
 */
class SDAnnouncementBar extends Component {
  /** @type {HTMLElement[]} */
  slides = [];

  /** @type {number} */
  currentIndex = 0;

  /** @type {number|null} Active setInterval handle, or null when idle. */
  interval = null;

  /** @type {number|null} Rotation delay in ms, or null when autoplay is off. */
  speed = null;

  /** @type {boolean} Desired autoplay state (visitor/OS intent). */
  playing = false;

  /** @type {boolean} */
  isTickerMode = false;

  /** @type {HTMLButtonElement|null} */
  playbackToggle = null;

  /** @type {HTMLElement|null} Visually-hidden polite live region (carousel mode). */
  liveRegion = null;

  /** @type {HTMLElement|null} Ticker track (the `--slides` container) in ticker mode. */
  tickerTrack = null;

  /** @type {(() => void)|null} Bound resize handler for ticker mode. */
  tickerResizeHandler = null;

  /** @type {(() => void)|null} Bound listener for the shared reduced-motion query. */
  motionPreferenceHandler = null;

  /** @type {boolean} True once the visitor has used the pause/play control. */
  userSetPlayback = false;

  /** @type {ResizeObserver|null} Publishes the bar's height for the transparent-header offset. */
  heightObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#trackHeight();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearInterval();
    if (this.tickerResizeHandler) {
      window.removeEventListener("resize", this.tickerResizeHandler);
      this.tickerResizeHandler = null;
    }
    if (this.motionPreferenceHandler) {
      reducedMotion.removeEventListener("change", this.motionPreferenceHandler);
      this.motionPreferenceHandler = null;
    }
    this.heightObserver?.disconnect();
    this.heightObserver = null;
    publishBarOffset();
  }

  #trackHeight() {
    const section = this.closest(".shopify-section");
    if (!(section instanceof HTMLElement)) return;

    this.heightObserver = new ResizeObserver(publishBarOffset);
    this.heightObserver.observe(section);
  }

  #init() {
    this.isTickerMode = this.hasAttribute("data-ticker");

    if (this.isTickerMode) {
      this.#initTicker();
    } else {
      this.#initCarousel();
    }
  }

  /**
   * React to runtime changes of the OS reduced-motion setting via the shared
   * `reducedMotion` MediaQueryList (from @theme/utilities — one cached query for
   * the whole theme). Unless the visitor has explicitly used the pause/play
   * control, follow their new preference.
   */
  #watchMotionPreference() {
    this.motionPreferenceHandler = () => {
      if (this.userSetPlayback) return;
      this.playing = !prefersReducedMotion();
      this.#applyMotion();
      this.#reflectPlaybackState();
    };
    reducedMotion.addEventListener("change", this.motionPreferenceHandler);
  }

  #initCarousel() {
    const slidesContainer = this.querySelector(".sd-announcement-bar__slides");
    if (!slidesContainer) return;

    this.slides = Array.from(
      slidesContainer.querySelectorAll(".sd-announcement-bar__slide"),
    );

    if (this.slides.length <= 1) return;

    // Navigation arrows
    const prevBtn = this.querySelector(".sd-announcement-bar__arrow--prev");
    const nextBtn = this.querySelector(".sd-announcement-bar__arrow--next");
    prevBtn?.addEventListener("click", () => this.#prev());
    nextBtn?.addEventListener("click", () => this.#next());

    // Pause/play control (WCAG 2.2.2 Pause, Stop, Hide)
    this.playbackToggle = this.querySelector("[data-playback-toggle]");
    this.playbackToggle?.addEventListener("click", () => this.#togglePlayback());

    // Live region for slide-change announcements (WAI-ARIA carousel pattern).
    this.liveRegion = this.querySelector("[data-live-region]");

    // WCAG 2.2.2: never rotate faster than 5s, regardless of the setting value.
    const configured = parseInt(this.dataset.autoplay ?? "", 10);
    this.speed = Number.isNaN(configured) ? null : Math.max(5, configured) * 1000;

    // WCAG 2.3.3: honor reduced-motion by resting on the first slide. The
    // control is still available so a visitor can opt into rotation.
    if (this.speed && !prefersReducedMotion()) {
      this.#play();
    } else {
      this.#reflectPlaybackState();
    }

    // Follow the OS reduced-motion setting if it changes mid-session.
    if (this.speed) this.#watchMotionPreference();

    // Pointer/focus temporarily suspends rotation without overriding an
    // explicit pause choice.
    this.addEventListener("mouseenter", () => this.#suspend());
    this.addEventListener("mouseleave", () => this.#resume());
    this.addEventListener("focusin", () => this.#suspend());
    this.addEventListener("focusout", () => this.#resume());

    this.#initSwipe();
  }

  /**
   * Touch-swipe navigation. Arrows are hidden below 749px, so a horizontal
   * swipe moves between slides. Vertical page scrolling is left untouched
   * (we never preventDefault on move).
   */
  #initSwipe() {
    const SWIPE_THRESHOLD = 40;
    let startX = 0;
    let startY = 0;
    let suppressClick = false;

    this.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "touch") return;
      startX = event.clientX;
      startY = event.clientY;
      suppressClick = false;
      this.#suspend();
    });

    this.addEventListener("pointerup", (event) => {
      if (event.pointerType !== "touch") return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        suppressClick = true;
        if (dx < 0) {
          this.#next();
        } else {
          this.#prev();
        }
      }
      this.#resume();
    });

    this.addEventListener("pointercancel", (event) => {
      if (event.pointerType !== "touch") return;
      this.#resume();
    });

    // Swallow the click that follows a swipe so it doesn't open the slide link.
    this.addEventListener(
      "click",
      (event) => {
        if (suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          suppressClick = false;
        }
      },
      true,
    );
  }

  #initTicker() {
    const container = this.querySelector(".sd-announcement-bar__slides");
    if (!container) return;
    this.tickerTrack = container;

    const group = container.querySelector("[data-ticker-group]");
    if (!group) return;

    // WCAG 2.2.2: the same pause/play control the carousel uses, so keyboard and
    // touch visitors can stop the marquee (CSS :hover alone reaches neither).
    this.playbackToggle = this.querySelector("[data-playback-toggle]");
    this.playbackToggle?.addEventListener("click", () => this.#togglePlayback());

    // WCAG 2.3.3: start paused under reduced motion; the control is the opt-in.
    // Otherwise scroll by default. `#applyMotion()` reflects this onto the track.
    this.playing = !prefersReducedMotion();

    // Follow the OS reduced-motion setting if it changes mid-session.
    this.#watchMotionPreference();

    const build = () => {
      // Guard against callbacks (rAF, fonts.ready, resize) firing after the
      // component has been removed — realistic in the theme editor.
      if (!this.isConnected) return;
      this.#buildTicker(container, group);
      this.#applyMotion();
      this.#reflectPlaybackState();
    };

    // Measure after the first layout so the group width is accurate.
    requestAnimationFrame(build);

    // Rebuild once web fonts finish loading — they can change the group width
    // and leave the marquee a clone short.
    document.fonts?.ready.then(build);

    let resizeRaf = null;
    this.tickerResizeHandler = () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(build);
    };
    window.addEventListener("resize", this.tickerResizeHandler);
  }

  /**
   * Clone the authoritative slide group enough times to cover the viewport,
   * then drive a seamless one-group-width scroll. Rebuildable on resize.
   *
   * @param {HTMLElement} container
   * @param {HTMLElement} group
   */
  #buildTicker(container, group) {
    // Read first (the original group's width and the visible bar width are both
    // independent of the clones), then mutate — avoids a forced reflow between
    // removing and re-adding clones.
    const groupWidth = group.getBoundingClientRect().width;
    if (!groupWidth) return;

    // Width of the visible bar (not the max-content track).
    const viewport = (this.closest(".sd-announcement-bar") ?? this).clientWidth;

    // Clear previous clones so a resize rebuilds from a clean group.
    for (const clone of container.querySelectorAll("[data-ticker-clone]")) {
      clone.remove();
    }

    // Enough clones that the track always covers the viewport through a
    // one-group shift — at least one clone for the seamless wrap.
    const cloneCount = Math.max(1, Math.ceil(viewport / groupWidth));
    for (let i = 0; i < cloneCount; i++) {
      const clone = group.cloneNode(true);
      // cloneNode copies every attribute, so drop the authoritative marker —
      // `data-ticker-group` must match exactly one element, `data-ticker-clone`
      // the copies (kept disjoint so teardown/lookup never confuse them).
      clone.removeAttribute("data-ticker-group");
      clone.setAttribute("data-ticker-clone", "");
      clone.setAttribute("aria-hidden", "true");
      for (const link of clone.querySelectorAll("a")) {
        link.setAttribute("tabindex", "-1");
      }
      container.appendChild(clone);
    }

    // 50px/second, at least 10s per loop; one loop == one group width.
    const duration = Math.max(groupWidth / 50, 10);
    container.style.setProperty("--ticker-shift", `-${groupWidth}px`);
    container.style.setProperty("--ticker-duration", `${duration}s`);
    container.setAttribute("data-ticker-ready", "");
  }

  #prev() {
    this.currentIndex =
      this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1;
    this.#updateSlides();
  }

  #next() {
    this.currentIndex =
      this.currentIndex === this.slides.length - 1 ? 0 : this.currentIndex + 1;
    this.#updateSlides();
  }

  #updateSlides() {
    for (const [index, slide] of this.slides.entries()) {
      // `inert` (not `aria-hidden`) also removes inactive slides — including
      // their links — from the tab order.
      slide.toggleAttribute("inert", index !== this.currentIndex);
    }
    this.#announceCurrentSlide();
  }

  /**
   * Push the active announcement's text into the live region. Whether it is
   * spoken depends on the region's `aria-live` state, which `#startInterval` /
   * `#clearInterval` flip: silent ("off") while auto-rotation is firing, spoken
   * ("polite") when paused, hover/focus-suspended, or manually navigated — so a
   * deliberate arrow/swipe is announced but the 5s auto-advance is not.
   */
  #announceCurrentSlide() {
    if (!this.liveRegion) return;
    const active = this.slides[this.currentIndex];
    const text = active
      ?.querySelector(".sd-announcement-bar__text")
      ?.textContent?.trim();
    this.liveRegion.textContent = text ?? "";
  }

  #startInterval() {
    if (!this.interval && this.speed) {
      this.interval = setInterval(() => this.#next(), this.speed);
      // Auto-advance is firing — keep the live region silent (WAI-ARIA: don't
      // interrupt AT on every rotation).
      this.liveRegion?.setAttribute("aria-live", "off");
    }
  }

  #clearInterval() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    // Not auto-advancing (paused, suspended, or manual) — announce changes.
    this.liveRegion?.setAttribute("aria-live", "polite");
  }

  #play() {
    this.playing = true;
    this.#applyMotion();
    this.#reflectPlaybackState();
  }

  #pause() {
    this.playing = false;
    this.#applyMotion();
    this.#reflectPlaybackState();
  }

  /**
   * Map the `playing` intent onto whichever motion mechanism is live: the
   * carousel's setInterval, or the ticker's CSS animation (paused via a
   * `data-paused` attribute so the keyframes stay owned by CSS, not JS).
   */
  #applyMotion() {
    if (this.isTickerMode) {
      this.tickerTrack?.toggleAttribute("data-paused", !this.playing);
    } else if (this.playing) {
      this.#startInterval();
    } else {
      this.#clearInterval();
    }
  }

  #togglePlayback() {
    // An explicit choice wins over later OS reduced-motion changes.
    this.userSetPlayback = true;
    if (this.playing) {
      this.#pause();
    } else {
      this.#play();
    }
  }

  /** Temporary suspend on hover/focus — keeps the visitor's play intent. */
  #suspend() {
    this.#clearInterval();
  }

  /** Resume after hover/focus, unless the visitor explicitly paused. */
  #resume() {
    if (this.playing) this.#startInterval();
  }

  #reflectPlaybackState() {
    const toggle = this.playbackToggle;
    if (!toggle) return;

    const label = this.playing
      ? toggle.dataset.labelPause
      : toggle.dataset.labelPlay;
    if (label) toggle.setAttribute("aria-label", label);

    const pauseIcon = toggle.querySelector(
      ".sd-announcement-bar__playback-icon--pause",
    );
    const playIcon = toggle.querySelector(
      ".sd-announcement-bar__playback-icon--play",
    );
    pauseIcon?.toggleAttribute("hidden", !this.playing);
    playIcon?.toggleAttribute("hidden", this.playing);
  }
}

if (!customElements.get("sd-announcement-bar")) {
  customElements.define("sd-announcement-bar", SDAnnouncementBar);
}
